import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  AppointmentRule,
  CreateAppointmentRuleRequest,
  UpdateAppointmentRuleRequest,
} from 'models/appointments';
import { AppointmentRuleType, appointmentRuleTypeLabels } from 'models/appointments';

export const APPT_RULES_RULE_TYPE_LABEL = 'Rule type';
export const APPT_RULES_MIN_NOTICE_LABEL = 'Minimum notice (hours)';
export const APPT_RULES_MIN_NOTICE_REQUIRED = 'Minimum notice is required.';
export const APPT_RULES_MIN_NOTICE_INVALID = 'Minimum notice must be a whole number greater than 0.';
export const APPT_RULES_SAVE_LABEL = 'Save Rule';
export const APPT_RULES_SAVING_LABEL = 'Saving…';
export const APPT_RULES_CANCEL_LABEL = 'Cancel';

/** Validation message for the raw hours input, or null when valid. */
function validateMinimumNotice(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return APPT_RULES_MIN_NOTICE_REQUIRED;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed <= 0) return APPT_RULES_MIN_NOTICE_INVALID;
  return null;
}

type Props = {
  /** Existing rule to edit; null/undefined renders the create form. */
  initialValue?: AppointmentRule | null;
  /** Disables inputs/buttons while the parent's mutation is in flight. */
  isSubmitting?: boolean;
  /** Server-side failure message — rendered while the user's input is preserved. */
  serverError?: string | null;
  /** Emits the create payload, or the update payload (+ isActive) when editing. */
  onSave: (payload: CreateAppointmentRuleRequest | UpdateAppointmentRuleRequest) => void;
  onCancel: () => void;
};

/**
 * Controlled appointment-rule form with hand-rolled touched-state validation
 * (plan Decision 3 — no react-hook-form/zod in this repo). The hours value is
 * kept as a string so empty is distinguishable from 0, and the positive-integer
 * guard runs before `onSave` is ever invoked — the serialized rule
 * configuration is derived from this single field only at submit time.
 * The rule-type select is wired so future rule types slot in by removing
 * `disabled` (label via `appointmentRuleTypeLabels`, never the raw enum).
 */
export default function AppointmentRuleForm({
  initialValue = null,
  isSubmitting = false,
  serverError = null,
  onSave,
  onCancel,
}: Props) {
  const [ruleType] = useState<AppointmentRuleType>(
    initialValue?.ruleType ?? AppointmentRuleType.TimeBasedRestriction,
  );
  const [hoursInput, setHoursInput] = useState<string>(
    initialValue?.minimumNoticeHours?.toString() ?? '',
  );
  const [touched, setTouched] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Move focus into the editor panel when it opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const validationError = validateMinimumNotice(hoursInput);
  const showValidationError = touched && validationError !== null;

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setTouched(true);
    // Pre-submit guard: empty/0/negative/decimal/non-numeric are rejected here,
    // before any payload is emitted (the server 422 is the backstop, not the guard).
    if (validateMinimumNotice(hoursInput) !== null) return;
    const minimumNoticeHours = Number(hoursInput.trim());
    if (initialValue) {
      onSave({ ruleType, minimumNoticeHours, isActive: initialValue.isActive });
    } else {
      onSave({ ruleType, minimumNoticeHours });
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-3">
      <div>
        <label htmlFor="appointment-rule-type" className="block text-xs text-gray-600 mb-1">
          {APPT_RULES_RULE_TYPE_LABEL}
        </label>
        <select
          id="appointment-rule-type"
          value={ruleType}
          disabled
          className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight bg-gray-50 focus:outline-none focus:shadow-outline disabled:cursor-not-allowed"
        >
          {(Object.keys(appointmentRuleTypeLabels) as AppointmentRuleType[]).map((type) => (
            <option key={type} value={type}>
              {appointmentRuleTypeLabels[type]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="appointment-rule-min-notice" className="block text-xs text-gray-600 mb-1">
          {APPT_RULES_MIN_NOTICE_LABEL}
        </label>
        <input
          ref={inputRef}
          id="appointment-rule-min-notice"
          type="text"
          inputMode="numeric"
          value={hoursInput}
          onChange={(e) => setHoursInput(e.target.value)}
          onBlur={() => setTouched(true)}
          disabled={isSubmitting}
          aria-invalid={showValidationError}
          aria-describedby={showValidationError ? 'appointment-rule-min-notice-error' : undefined}
          className={`shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline disabled:cursor-not-allowed disabled:opacity-50 ${
            showValidationError ? 'border-red-500' : ''
          }`}
        />
        {showValidationError && (
          <p id="appointment-rule-min-notice-error" className="mt-1 text-xs text-red-600">
            {validationError}
          </p>
        )}
      </div>

      {serverError && (
        <p role="alert" className="text-sm text-red-600">
          {serverError}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSubmitting ? APPT_RULES_SAVING_LABEL : APPT_RULES_SAVE_LABEL}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="inline-flex items-center justify-center rounded-lg bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {APPT_RULES_CANCEL_LABEL}
        </button>
      </div>
    </form>
  );
}
